import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faBookOpen,
  faCalendarCheck,
  faCheckCircle,
  faClipboardList,
  faDatabase,
  faExclamationTriangle,
  faFileAlt,
  faFileInvoiceDollar,
  faMoneyBillWave,
  faPlay,
  faSpinner,
  faStethoscope,
  faSyncAlt,
  faTools,
  faUserMd,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import {
  extractBackendClaims,
  runBackendModuleAction,
} from "../../services/backendModuleApi";
import { BASE_URL } from "../../config";
import BatchFileSpecsModal from "../Member/BatchFileSpecsModal";

const BACKEND_MODULE_SPECS_ENDPOINT = `${BASE_URL}/backend-modules/specs`;
const BACKEND_MODULE_SPECS_PAGE = "/backend/specs";

const COVERAGE_OPTIONS = [
  { value: "OUTPATIENT-CLINIC-SETTING", label: "OUTPATIENT-CLINIC-SETTING" },
  { value: "OUTPATIENT-LABORATORY", label: "OUTPATIENT-LABORATORY" },
];

const PAYMENT_OPTIONS = [
  { value: "HB", label: "Paid to Provider" },
  { value: "PF", label: "Paid to Doctor" },
];

const WHT_OPTIONS = [
  { value: "remove", label: "Remove WHT" },
  { value: "add", label: "Add WHT" },
];

const GROUPS = [
  { id: "core", title: "Core", icon: faDatabase },
  { id: "ape", title: "APE", icon: faCalendarCheck },
  { id: "provider_member", title: "Provider and Member", icon: faUserMd },
  { id: "finance", title: "Finance", icon: faMoneyBillWave },
  { id: "documents", title: "Documents", icon: faFileAlt },
  { id: "disabled", title: "Disabled", icon: faBan },
];

const OPERATIONS = [
  {
    action: "backend_revert",
    group: "core",
    title: "Backend Revert",
    icon: faSyncAlt,
    fields: [{ name: "trans_loa_no", label: "LOA/TRN#", required: true }],
  },
  {
    action: "change_coverage",
    group: "core",
    title: "Change Coverage",
    icon: faStethoscope,
    fields: [
      { name: "trans_loa_no", label: "LOA/TRN#", required: true },
      {
        name: "coverage_code",
        label: "Change to Coverage",
        required: true,
        type: "select",
        options: COVERAGE_OPTIONS,
      },
    ],
  },
  {
    action: "ape_change_provider",
    group: "ape",
    title: "APE Change Provider",
    icon: faCalendarCheck,
    fields: [
      { name: "member_ids", label: "Member ID(s)", required: true, placeholder: "Comma-separated" },
      { name: "provider_code", label: "New Provider Code", required: true },
    ],
  },
  {
    action: "ape_change_amount",
    group: "ape",
    title: "APE Change Amount",
    icon: faCalendarCheck,
    requiresOneOf: ["loa_nos", "member_ids"],
    fields: [
      { name: "loa_nos", label: "LOA#(s)", placeholder: "Comma-separated" },
      { name: "member_ids", label: "Member ID(s)", placeholder: "Comma-separated" },
      { name: "amount", label: "New Amount", required: true, type: "number" },
    ],
  },
  {
    action: "ape_extension",
    group: "ape",
    title: "APE Extension",
    icon: faCalendarCheck,
    requiresOneOf: ["corp_codes", "member_ids"],
    fields: [
      { name: "corp_codes", label: "Corporate Code(s)", placeholder: "Comma-separated" },
      { name: "member_ids", label: "Member ID(s)", placeholder: "Comma-separated" },
      { name: "end_date", label: "End Date", required: true, type: "date" },
    ],
  },
  {
    action: "change_provider",
    group: "provider_member",
    title: "Backend Change Provider",
    icon: faUserMd,
    fields: [
      { name: "trans_loa_no", label: "LOA/TRN#", required: true },
      { name: "provider_code", label: "New Provider Code", required: true },
    ],
  },
  {
    action: "change_member",
    group: "provider_member",
    title: "Backend Change Member",
    icon: faClipboardList,
    fields: [
      { name: "trans_loa_no", label: "LOA/TRN#", required: true },
      { name: "member_id", label: "New Member ID", required: true },
    ],
  },
  {
    action: "transfer_physician_delete",
    group: "provider_member",
    title: "Transfer Doctor's TRN Then Delete",
    icon: faUserMd,
    fields: [
      { name: "old_physician_id", label: "Old Physician ID", required: true },
      { name: "new_physician_id", label: "New Physician ID", required: true },
    ],
  },
  {
    action: "change_payment_setup",
    group: "finance",
    title: "Change Payment Setup",
    icon: faFileInvoiceDollar,
    fields: [
      { name: "loa_no", label: "LOA#", required: true, maxLength: 7 },
      {
        name: "method",
        label: "Payment Setup",
        required: true,
        type: "select",
        options: PAYMENT_OPTIONS,
      },
    ],
  },
  {
    action: "transfer_loa_to_prn",
    group: "finance",
    title: "Transfer LOA to PRN",
    icon: faMoneyBillWave,
    fields: [
      { name: "loa_no", label: "LOA#", required: true, maxLength: 7 },
      { name: "prn", label: "To PRN", required: true },
    ],
  },
  {
    action: "remove_loa_from_prn",
    group: "finance",
    title: "Remove LOA From PRN",
    icon: faMoneyBillWave,
    fields: [{ name: "loa_no", label: "LOA#", required: true, maxLength: 7 }],
  },
  {
    action: "change_soa",
    group: "finance",
    title: "Change SOA#",
    icon: faFileInvoiceDollar,
    fields: [
      { name: "loa_no", label: "LOA#", required: true, maxLength: 7 },
      { name: "soa", label: "Correct SOA#", required: true },
    ],
  },
  {
    action: "wht",
    group: "finance",
    title: "WHT Remove/Add",
    icon: faMoneyBillWave,
    fields: [
      { name: "loa_nos", label: "LOA#(s)", required: true, placeholder: "Comma-separated" },
      {
        name: "wht_action",
        label: "WHT",
        required: true,
        type: "select",
        options: WHT_OPTIONS,
      },
    ],
  },
  {
    action: "remove_payment_details",
    group: "finance",
    title: "Remove Payment Details",
    icon: faMoneyBillWave,
    fields: [{ name: "prn", label: "PRN", required: true }],
  },
  {
    action: "generate_settlement",
    group: "finance",
    title: "Generate Settlement",
    icon: faSyncAlt,
    fields: [{ name: "prn", label: "PRN", required: true }],
  },
  {
    action: "remove_deposit_slip",
    group: "finance",
    title: "Remove Deposit Slip",
    icon: faFileInvoiceDollar,
    fields: [{ name: "prn", label: "PRN", required: true }],
  },
  {
    action: "remove_ppd",
    group: "finance",
    title: "Remove PPD",
    icon: faMoneyBillWave,
    fields: [{ name: "loa_nos", label: "LOA#(s)", required: true, placeholder: "Comma-separated" }],
  },
  {
    action: "regenerate_form_2307",
    group: "documents",
    title: "Regenerate Form 2307",
    icon: faFileAlt,
    fields: [{ name: "prn", label: "PRN", required: true }],
  },
  {
    action: "regenerate_pis",
    group: "documents",
    title: "Regenerate PIS",
    icon: faFileAlt,
    fields: [{ name: "prn", label: "PRN", required: true }],
  },
  {
    action: "backdate_disabled",
    group: "disabled",
    title: "LOA Backdate",
    icon: faBan,
    disabled: true,
    fields: [
      { name: "loa_no", label: "LOA#" },
      { name: "date", label: "Date", type: "date" },
    ],
  },
];

const buildInitialForms = () => {
  const next = {};

  OPERATIONS.forEach((operation) => {
    next[operation.action] = {};
    operation.fields.forEach((field) => {
      if (field.type === "select") {
        next[operation.action][field.name] = field.options?.[0]?.value || "";
      } else {
        next[operation.action][field.name] = "";
      }
    });
  });

  return next;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getFieldDisplayValue = (field, value) => {
  const rawValue = String(value ?? "").trim();
  if (!rawValue) {
    return "";
  }

  if (field.type === "select") {
    const option = field.options?.find((item) => item.value === rawValue);
    return option ? `${option.label} (${option.value})` : rawValue;
  }

  return rawValue;
};

const buildConfirmationHtml = (operation, values) => {
  const rows = operation.fields
    .map((field) => ({
      label: field.label,
      value: getFieldDisplayValue(field, values[field.name]),
    }))
    .filter((row) => row.value)
    .map(
      (row) => `
        <tr>
          <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;color:#4b5563;">${escapeHtml(row.label)}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #e5e7eb;font-weight:600;color:#111827;">${escapeHtml(row.value)}</td>
        </tr>
      `
    )
    .join("");

  const inputSummary = rows
    ? `<table style="width:100%;border-collapse:collapse;margin-top:10px;font-size:13px;">${rows}</table>`
    : `<p style="margin-top:10px;color:#4b5563;">No input fields for this action.</p>`;

  return `<div style="text-align:left;font-size:14px;line-height:1.45;">
    <p style="margin:0 0 8px;"><strong>Action:</strong> ${escapeHtml(operation.title)}</p>
    <p style="margin:0 0 8px;">Pakicheck muna ang details sa baba bago ituloy. This action can update, delete, or regenerate backend records depending on the selected tool.</p>
    ${inputSummary}
    <p style="margin:12px 0 0;color:#b45309;font-weight:600;">Confirm only if the LOA/TRN/PRN and payment status were already verified.</p>
  </div>`;
};

const BackendModulePage = () => {
  const operationsByGroup = useMemo(() => {
    return GROUPS.map((group) => ({
      ...group,
      operations: OPERATIONS.filter((operation) => operation.group === group.id),
    })).filter((group) => group.operations.length > 0);
  }, []);

  const [forms, setForms] = useState(buildInitialForms);
  const [loadingAction, setLoadingAction] = useState(null);
  const [lastResults, setLastResults] = useState({});
  const [extractLoading, setExtractLoading] = useState(false);
  const [showSpecsModal, setShowSpecsModal] = useState(false);

  const updateField = (action, field, value) => {
    setForms((prev) => ({
      ...prev,
      [action]: {
        ...(prev[action] || {}),
        [field]: value,
      },
    }));
  };

  const resetOperation = (operation) => {
    setForms((prev) => ({
      ...prev,
      [operation.action]: buildInitialForms()[operation.action],
    }));
  };

  const validateOperation = (operation) => {
    const values = forms[operation.action] || {};

    for (const field of operation.fields) {
      if (field.required && !String(values[field.name] || "").trim()) {
        toast.error(`${field.label} is required.`);
        return false;
      }
    }

    if (operation.requiresOneOf) {
      const hasOne = operation.requiresOneOf.some((name) =>
        String(values[name] || "").trim()
      );
      if (!hasOne) {
        toast.error("Enter at least one source field.");
        return false;
      }
    }

    return true;
  };

  const handleRun = async (operation) => {
    if (operation.disabled) {
      toast.error("LOA Backdate is disabled.");
      return;
    }

    if (!validateOperation(operation)) {
      return;
    }

    const confirm = await Swal.fire({
      title: "Confirm Action",
      html: buildConfirmationHtml(operation, forms[operation.action] || {}),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm Run",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setLoadingAction(operation.action);

    try {
      const response = await runBackendModuleAction(
        operation.action,
        forms[operation.action] || {}
      );
      const result = response.data || {};
      const legacy = String(result.legacy_result || "");
      const message = result.message || "Done";

      setLastResults((prev) => ({
        ...prev,
        [operation.action]: {
          status: legacy.includes("has_payment") || legacy.includes("INVALID") || legacy.includes("not found") ? "warning" : "success",
          message,
          legacy,
          data: result.data || null,
          at: new Date().toLocaleString(),
        },
      }));

      if (legacy.includes("has_payment_override")) {
        toast.success("Done, payment details were removed.");
      } else if (legacy.includes("has_payment") || legacy.includes("already have payment details")) {
        toast.error("LOA#s has payment details already.");
      } else if (legacy.includes("INVALID COVERAGE")) {
        toast.error("Invalid LOA# or Coverage.");
      } else if (legacy.includes("same coverage")) {
        toast.error("Failed to change coverage - same coverage.");
      } else if (legacy.includes("not found")) {
        toast.error(message);
      } else {
        toast.success(message);
        resetOperation(operation);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Error Processing";
      setLastResults((prev) => ({
        ...prev,
        [operation.action]: {
          status: "error",
          message,
          legacy: "",
          data: null,
          at: new Date().toLocaleString(),
        },
      }));
      toast.error(message);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleExtractClaims = async () => {
    const confirm = await Swal.fire({
      title: "Extract Claims",
      html: `<div style="text-align:left;font-size:14px;line-height:1.45;">
        <p style="margin:0 0 8px;"><strong>Action:</strong> Extract Claims</p>
        <p style="margin:0 0 8px;">This will run claims extraction and settlement preparation. Claims with missing required information will be held while independent valid claims continue. Review Extract Claims Queue for reasons and next steps.</p>
        <p style="margin:12px 0 0;color:#b45309;font-weight:600;">Confirm only if pending claims are ready for extraction.</p>
      </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm Extract",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#7c3aed",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setExtractLoading(true);
    try {
      const response = await extractBackendClaims();
      if (response.data?.status === "partial") {
        toast(response.data.message, { icon: "\u26a0\ufe0f", duration: 8000 });
      } else {
        toast.success(response.data?.message || "Done Extracting");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error extracting, please try again.");
    } finally {
      setExtractLoading(false);
    }
  };

  const renderField = (operation, field) => {
    const value = forms[operation.action]?.[field.name] ?? "";
    const baseClass =
      "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100 disabled:text-gray-500";

    if (field.type === "select") {
      return (
        <select
          className={baseClass}
          value={value}
          disabled={operation.disabled || loadingAction === operation.action}
          onChange={(event) => updateField(operation.action, field.name, event.target.value)}
        >
          {(field.options || []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.type || "text"}
        maxLength={field.maxLength}
        className={baseClass}
        value={value}
        placeholder={field.placeholder || ""}
        disabled={operation.disabled || loadingAction === operation.action}
        onChange={(event) => updateField(operation.action, field.name, event.target.value)}
      />
    );
  };

  const renderOperation = (operation) => {
    const result = lastResults[operation.action];
    const isLoading = loadingAction === operation.action;

    return (
      <section
        key={operation.action}
        className={`rounded-md border bg-white p-4 shadow-sm ${
          operation.disabled ? "border-gray-200 bg-gray-50" : "border-gray-200"
        }`}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div
              className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md ${
                operation.disabled ? "bg-gray-200 text-gray-500" : "bg-blue-50 text-blue-700"
              }`}
            >
              <FontAwesomeIcon icon={operation.icon} />
            </div>
            <div className="min-w-0">
              <h3 className="break-words text-sm font-semibold text-gray-900">
                {operation.title}
              </h3>
              {operation.disabled && (
                <span className="mt-1 inline-flex rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-600">
                  Disabled
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {operation.fields.map((field) => (
            <label key={field.name} className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-600">
                {field.label}
                {field.required && <span className="text-red-600"> *</span>}
              </span>
              {renderField(operation, field)}
            </label>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
          <button
            type="button"
            onClick={() => resetOperation(operation)}
            disabled={operation.disabled || isLoading}
            className="rounded-md border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => handleRun(operation)}
            disabled={operation.disabled || isLoading}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {isLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faPlay} />
            )}
            Run
          </button>
        </div>

        {result && (
          <div
            className={`mt-3 rounded-md border px-3 py-2 text-xs ${
              result.status === "success"
                ? "border-green-200 bg-green-50 text-green-800"
                : result.status === "warning"
                ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-red-200 bg-red-50 text-red-800"
            }`}
          >
            <div className="flex items-center gap-2 font-semibold">
              <FontAwesomeIcon
                icon={result.status === "success" ? faCheckCircle : faExclamationTriangle}
              />
              <span>{result.message}</span>
            </div>
            {result.legacy && <div className="mt-1 font-mono">{result.legacy}</div>}
            <div className="mt-1 text-[11px] opacity-80">{result.at}</div>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <header className="mb-5 flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-900 text-white">
              <FontAwesomeIcon icon={faTools} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-950">Backend</h1>
              <p className="text-sm text-gray-500">Version 2.0 operations</p>
            </div>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto">
            <a href="/claims/extract-queue" className="inline-flex items-center justify-center gap-2 rounded-md border border-emerald-300 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-800">
              <FontAwesomeIcon icon={faClipboardList} />
              Extract Claims Queue
            </a>
            <button
              type="button"
              onClick={() => setShowSpecsModal(true)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
            >
              <FontAwesomeIcon icon={faBookOpen} />
              View Full Specs
            </button>
            <button
              type="button"
              onClick={handleExtractClaims}
              disabled={extractLoading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-purple-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-purple-800 disabled:cursor-not-allowed disabled:bg-gray-300 md:w-auto"
            >
              {extractLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faSyncAlt} />
              )}
              Extract Claims
            </button>
          </div>
        </header>

        <div className="space-y-6">
          {operationsByGroup.map((group) => (
            <section key={group.id}>
              <div className="mb-3 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-200 text-gray-700">
                  <FontAwesomeIcon icon={group.icon} className="text-xs" />
                </div>
                <h2 className="text-sm font-bold uppercase tracking-wide text-gray-700">
                  {group.title}
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {group.operations.map(renderOperation)}
              </div>
            </section>
          ))}
        </div>
      </div>

      <BatchFileSpecsModal
        endpoint={BACKEND_MODULE_SPECS_ENDPOINT}
        isOpen={showSpecsModal}
        onClose={() => setShowSpecsModal(false)}
        title="Backend Module Specs"
        fullPagePath={BACKEND_MODULE_SPECS_PAGE}
        fullPageLabel="View in Full page"
      />
    </div>
  );
};

export default BackendModulePage;
